import React, { useState } from 'react';
import PropTypes from 'prop-types';
import {
    Autocomplete,
    Box,
    InputAdornment,
    TextField,
} from '@mui/material';
import PublicOutlinedIcon from '@mui/icons-material/PublicOutlined';
import { getAppStyleTokens } from '../../styles/msiStyleTokens';
import {
    CountryChipLabel,
    getCountryChipDisplay,
} from '../shared/securityDisplayPrimitives';

export const ISO_ALPHA3_COUNTRY_CODES = `ABW AFG AGO AIA ALA ALB AND ARE ARG ARM ASM ATA ATF ATG AUS AUT AZE BDI BEL BEN BES BFA BGD BGR BHR BHS BIH BLM BLR BLZ BMU BOL BRA BRB BRN BTN BVT BWA CAF CAN CCK CHE CHL CHN CIV CMR COD COG COK COL COM CPV CRI CUB CUW CXR CYM CYP CZE DEU DJI DMA DNK DOM DZA ECU EGY ERI ESH ESP EST ETH FIN FJI FLK FRA FRO FSM GAB GBR GEO GGY GHA GIB GIN GLP GMB GNB GNQ GRC GRD GRL GTM GUF GUM GUY HKG HMD HND HRV HTI HUN IDN IMN IND IOT IRL IRN IRQ ISL ISR ITA JAM JEY JOR JPN KAZ KEN KGZ KHM KIR KNA KOR KWT LAO LBN LBR LBY LCA LIE LKA LSO LTU LUX LVA MAC MAF MAR MCO MDA MDG MDV MEX MHL MKD MLI MLT MMR MNE MNG MNP MOZ MRT MSR MTQ MUS MWI MYS MYT NAM NCL NER NFK NGA NIC NIU NLD NOR NPL NRU NZL OMN PAK PAN PCN PER PHL PLW PNG POL PRI PRK PRT PRY PSE PYF QAT REU ROU RUS RWA SAU SDN SEN SGP SGS SHN SJM SLB SLE SLV SMR SOM SPM SRB SSD STP SUR SVK SVN SWE SWZ SXM SYC SYR TCA TCD TGO THA TJK TKL TKM TLS TON TTO TUN TUR TUV TWN TZA UGA UKR UMI URY USA UZB VAT VCT VEN VGB VIR VNM VUT WLF WSM YEM ZAF ZMB ZWE`.split(' ');

// Colors come from the active workspace theme's role contract, so the dialog
// follows every dark, light and high-contrast appearance.
const paletteRole = (role) => (theme) => getAppStyleTokens(theme).palette[role];
const componentRole = (role) => (theme) => getAppStyleTokens(theme).componentColors[role];

export const ADD_DIALOG_TOOLBAR_HEIGHT = 34;
const FIELD_HEIGHT = 42;
const FIELD_RADIUS = '10px';
const CONTROL_RADIUS = '8px';

export const addDialogTitleRowSx = {
    display: 'flex',
    alignItems: 'center',
    gap: 1,
    px: { xs: 2, sm: 3 },
    pt: { xs: 2, sm: 2.5 },
    pb: 1,
};

export const addDialogTitleSx = {
    flex: 1,
    minWidth: 0,
    p: 0,
    borderBottom: 'none',
    fontSize: { xs: '18px', sm: '20px' },
    fontWeight: 700,
    lineHeight: 1.25,
};

export const addDialogCloseButtonSx = {
    flexShrink: 0,
    border: 'none',
    borderRadius: CONTROL_RADIUS,
    bgcolor: 'transparent',
    color: 'text.secondary',
    '&:hover': {
        bgcolor: componentRole('secondaryHover'),
        color: 'text.primary',
    },
    '&.Mui-disabled': {
        border: 'none',
        bgcolor: 'transparent',
    },
};

export const addDialogContentSx = {
    display: 'grid',
    gap: 2,
    px: { xs: 2, sm: 3 },
    pt: 1,
    pb: 3,
};

export const addDialogTabsSx = {
    minHeight: 44,
    borderBottom: '1px solid',
    borderColor: paletteRole('line'),
    '& .MuiTabs-indicator': {
        height: 3,
        borderRadius: '3px 3px 0 0',
    },
    '& .MuiTab-root': {
        flex: { xs: 1, sm: 'none' },
        minHeight: 44,
        px: 2,
        py: 1,
        borderRadius: `${CONTROL_RADIUS} ${CONTROL_RADIUS} 0 0`,
        color: 'text.secondary',
        fontSize: '12.5px',
        fontWeight: 700,
        letterSpacing: '0.03em',
        '&:hover': {
            bgcolor: componentRole('secondaryHover'),
            color: 'text.primary',
        },
    },
    '& .MuiTab-root.Mui-selected': {
        bgcolor: paletteRole('blue100'),
        color: 'primary.main',
    },
};

export const addDialogFieldSx = {
    '& .MuiOutlinedInput-root': {
        minHeight: FIELD_HEIGHT,
        borderRadius: FIELD_RADIUS,
        fontSize: '14px',
    },
    '& .MuiInputAdornment-root': { color: 'text.secondary' },
};

export const addDialogToolbarFieldSx = {
    '& .MuiOutlinedInput-root': {
        minHeight: ADD_DIALOG_TOOLBAR_HEIGHT,
        borderRadius: CONTROL_RADIUS,
        fontSize: '13px',
    },
};

// Short values such as "Relevance" or "TICKER" need their label in view, so it
// sits above the outline instead of in a notch. Pair with
// InputLabelProps={{ shrink: true }} and InputProps={{ notched: false }}.
export const addDialogStaticLabelSx = {
    '& .MuiInputLabel-root': {
        position: 'relative',
        transform: 'none',
        maxWidth: '100%',
        mb: 0.5,
        color: 'text.secondary',
        fontSize: '12px',
        fontWeight: 600,
        lineHeight: 1.4,
    },
    '& .MuiInputLabel-root.Mui-focused': { color: 'primary.main' },
};

export const addDialogToggleSx = {
    minHeight: ADD_DIALOG_TOOLBAR_HEIGHT,
    px: 1.75,
    border: '1px solid',
    borderColor: paletteRole('line'),
    borderRadius: CONTROL_RADIUS,
    bgcolor: paletteRole('surfaceSubtle'),
    color: 'text.primary',
    fontSize: '12.5px',
    fontWeight: 700,
    whiteSpace: 'nowrap',
    '&:hover': {
        borderColor: componentRole('secondaryHoverBorder'),
        bgcolor: componentRole('secondaryHover'),
    },
    '&[aria-pressed="true"]:not(.Mui-disabled)': {
        borderColor: 'primary.main',
        bgcolor: paletteRole('blue100'),
        color: 'primary.main',
    },
};

export const addDialogButtonSx = {
    minHeight: 38,
    minWidth: 96,
    px: 2.25,
    borderRadius: CONTROL_RADIUS,
    fontSize: '13px',
    fontWeight: 700,
};

export const addDialogFieldButtonSx = {
    ...addDialogButtonSx,
    minHeight: FIELD_HEIGHT,
    whiteSpace: 'nowrap',
};

export const addDialogFooterSx = {
    px: { xs: 2, sm: 3 },
    py: 2,
    borderTop: '1px solid',
    borderColor: paletteRole('line'),
    bgcolor: paletteRole('surfaceSubtle'),
};

export const addDialogSoftCardSx = {
    p: { xs: 2, sm: 2.5 },
    borderRadius: '12px',
    borderColor: paletteRole('line'),
    bgcolor: paletteRole('surfaceSubtle'),
};

export const addDialogIconBadgeSx = {
    display: 'grid',
    placeItems: 'center',
    flexShrink: 0,
    width: 52,
    height: 52,
    borderRadius: '50%',
    bgcolor: paletteRole('blue100'),
    color: 'primary.main',
};

export const addDialogChoiceRowSx = {
    display: 'flex',
    alignItems: 'center',
    gap: 1,
    px: 1,
    py: 0.5,
    cursor: 'pointer',
    '&:hover': { bgcolor: componentRole('rowHover') },
};

export const addDialogSelectedChoiceRowSx = {
    bgcolor: paletteRole('blue100'),
    '&:hover': { bgcolor: paletteRole('blue100') },
};

/**
 * ISO alpha-3 country picker shared by both add modes. Options and the
 * committed value show their flag, while the input text stays the plain code
 * so typing "HKG" still matches and keyboard selection is unchanged.
 */
export function CountryCodeAutocomplete({
    label,
    placeholder = '',
    labelAbove = false,
    value,
    onChange,
    disabled = false,
    sx = undefined,
}) {
    const [inputValue, setInputValue] = useState(value || '');
    const selected = value ? getCountryChipDisplay(value) : null;
    const showSelectedFlag = Boolean(selected?.flag)
        && inputValue.trim().toUpperCase() === selected.label;
    return (
        <Autocomplete
            options={ISO_ALPHA3_COUNTRY_CODES}
            value={value || null}
            inputValue={inputValue}
            onInputChange={(_event, nextInput) => setInputValue(nextInput)}
            onChange={(_event, nextValue) => onChange(nextValue || '')}
            onKeyDown={(event) => {
                // Enter only picks the highlighted code (the Autocomplete still
                // handles it after this); it must never reach the dialog or an
                // enclosing form as a submit/close keystroke.
                if (event.key !== 'Enter') return;
                event.preventDefault();
                event.stopPropagation();
            }}
            disabled={disabled}
            autoHighlight
            clearOnBlur
            sx={sx}
            renderOption={(props, code) => {
                const { key, ...optionProps } = props;
                return (
                    <Box component="li" key={key} {...optionProps}>
                        <CountryChipLabel display={getCountryChipDisplay(code)} />
                    </Box>
                );
            }}
            renderInput={(params) => (
                <TextField
                    {...params}
                    label={labelAbove ? label : undefined}
                    placeholder={placeholder || label}
                    sx={labelAbove ? [addDialogFieldSx, addDialogStaticLabelSx] : addDialogFieldSx}
                    InputLabelProps={{ ...params.InputLabelProps, shrink: true }}
                    inputProps={labelAbove
                        ? params.inputProps
                        : { ...params.inputProps, 'aria-label': label }}
                    InputProps={{
                        ...params.InputProps,
                        notched: labelAbove ? false : undefined,
                        startAdornment: (
                            <InputAdornment position="start">
                                {showSelectedFlag ? (
                                    <Box
                                        component="span"
                                        aria-hidden="true"
                                        sx={{ fontSize: '18px', lineHeight: 1 }}
                                    >
                                        {selected.flag}
                                    </Box>
                                ) : (
                                    <PublicOutlinedIcon fontSize="small" />
                                )}
                            </InputAdornment>
                        ),
                    }}
                />
            )}
        />
    );
}

CountryCodeAutocomplete.propTypes = {
    label: PropTypes.string.isRequired,
    placeholder: PropTypes.string,
    labelAbove: PropTypes.bool,
    value: PropTypes.string.isRequired,
    onChange: PropTypes.func.isRequired,
    disabled: PropTypes.bool,
    sx: PropTypes.oneOfType([PropTypes.object, PropTypes.array, PropTypes.func]),
};
